import React from 'react'
import { Trophy, Sparkles, Gem } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const TopSkinsShowcase = ({ topSkins = [], isLoading = false }) => {
  const { t } = useTranslation()
  // Показываем топ-4
  const displayedSkins = topSkins.slice(0, 4)
  
  // Функция сокращения качества
  const shortenWear = (text) => {
    return text
      .replace(/\(Factory New\)/gi, 'FN')
      .replace(/\(Minimal Wear\)/gi, 'MW')
      .replace(/\(Field-Tested\)/gi, 'FT')
      .replace(/\(Well-Worn\)/gi, 'WW')
      .replace(/\(Battle-Scarred\)/gi, 'BS')
  }
  
  // Скелетон загрузки
  if (isLoading) {
    return (
      <div className="p-6 border-b border-white border-opacity-10">
        {/* Заголовок секции - скелетон */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2 justify-center">
            <div className="w-10 h-10 rounded-full bg-gray-600 animate-pulse"></div>
            <div className="h-8 w-40 bg-gray-600 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Сетка скинов - скелетон 2 в ряд */}
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((index) => (
            <div
              key={index}
              className="bg-surface rounded-xl border border-white border-opacity-10 overflow-hidden"
            >
              {/* Изображение скина - скелетон */}
              <div className="aspect-square p-4 flex items-center justify-center bg-gray-700 animate-pulse">
                <div className="w-full h-full bg-gray-600 rounded"></div>
              </div>

              {/* Информация о скине - скелетон */}
              <div className="p-3 bg-surface">
                <div className="mb-2 space-y-1">
                  <div className="h-3 bg-gray-600 rounded animate-pulse w-full"></div>
                  <div className="h-2 bg-gray-700 rounded animate-pulse w-2/3 mx-auto"></div>
                </div>
                
                {/* Цена - скелетон */}
                <div className="flex items-center justify-center gap-1">
                  <div className="h-4 w-16 bg-gray-600 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }
  
  if (!topSkins || topSkins.length === 0) {
    return (
      <div className="p-6 border-b border-white border-opacity-10">
        <div className="bg-gradient-to-br from-surface to-dark-primary rounded-2xl p-8 border border-white border-opacity-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent-primary/10 mb-4">
            <Trophy size={40} className="text-accent-primary" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{t('topSkins.empty')}</h3>
          <p className="text-text-secondary">{t('topSkins.emptyDescription')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 border-b border-white border-opacity-10">
      {/* Заголовок секции */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2 justify-center">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary">
            <Trophy size={20} className="text-dark-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              {t('topSkins.title')}
            </h2>
          </div>
        </div>
      </div>

      {/* Сетка скинов - 2 в ряд */}
      <div className="grid grid-cols-2 gap-3">
        {displayedSkins.map((skin, index) => {
          // Определяем цвета фона для топ-4
          const bgGradient = index === 0 
            ? 'from-yellow-500/20 to-yellow-600/10' 
            : index === 1
            ? 'from-gray-400/20 to-gray-500/10'
            : index === 2
            ? 'from-orange-500/20 to-orange-600/10'
            : 'from-blue-500/20 to-blue-600/10'
          
          const borderColor = index === 0
            ? 'border-yellow-500/30'
            : index === 1
            ? 'border-gray-400/30'
            : index === 2
            ? 'border-orange-500/30'
            : 'border-blue-500/30'
          
          const shadowColor = index === 0
            ? 'hover:shadow-[0_0_30px_rgba(234,179,8,0.4)]'
            : index === 1
            ? 'hover:shadow-[0_0_30px_rgba(156,163,175,0.4)]'
            : index === 2
            ? 'hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]'
            : 'hover:shadow-[0_0_30px_rgba(59,130,246,0.4)]'

          return (
            <div
              key={index}
              className={`group relative bg-gradient-to-br ${bgGradient} rounded-xl border ${borderColor} overflow-hidden transition-all duration-300 hover:scale-[1.03] ${shadowColor}`}
            >


              {/* Изображение скина */}
              <div className="aspect-square p-4 flex items-center justify-center">
                <img
                  src={skin.img}
                  alt={skin.mhn}
                  className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] group-hover:scale-110 transition-transform duration-300"
                  loading="lazy"
                />
              </div>

              {/* Информация о скине */}
              <div className={`p-3 bg-gradient-to-br ${bgGradient} backdrop-blur-sm text-center`}>
                <div className="mb-2">
                  {skin.mhn?.includes('|') ? (
                    <>
                      <div className="text-white text-xs font-medium leading-tight line-clamp-1">
                        {skin.mhn.split('|')[0].trim()}
                      </div>
                      <div className="text-text-secondary text-[10px] mt-0.5 font-normal">
                        {shortenWear(skin.mhn.split('|')[1].trim())}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-white font-medium line-clamp-2 leading-tight">
                      {skin.mhn}
                    </p>
                  )}
                </div>
                
                {/* Цена */}
                <div className="flex items-center justify-center gap-1">
                  <span className="text-accent-primary font-bold text-sm"><Gem size={12} /></span>
                  <span className="text-accent-primary font-bold text-sm">
                    {parseInt(skin.cost).toLocaleString('ru-RU')}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TopSkinsShowcase
